import React, { useMemo, useState } from "react";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { View } from "@common/types/views/views";
import { RuleGroupType } from "@common/types/transactionRule";
import { NormalizedDescriptionInfo } from "@common/types/normalizedDescription";
import { ViewConfig } from "@lib/views";
import { useNormalizedDescriptions } from "@hooks/useNormalizedDescriptions";
import historyDetailsStyles from "../HistoryDetails.module.scss";
import { RuleVersion } from "./views/historyRuleDetailsViewConfig";
import {
    buildRuleRowData,
    RuleDetailsRowData,
} from "./utils/historyRuleDetailsRows";
import { defaultHistoryRuleDetailsView } from "./views/defaultHistoryRuleDetailsView";
import { historyRuleDetailsTableViewConfig } from "./views/historyRuleDetailsTableViewConfig";
import { defaultTransferHistoryRuleDetailsView } from "./views/defaultTransferHistoryRuleDetailsView";
import { transferHistoryRuleDetailsTableViewConfig } from "./views/transferHistoryRuleDetailsTableViewConfig";

interface HistoryRuleDetailsProps {
    versions: RuleVersion[];
    footer: React.ReactNode;
}

function enrichRowWithNormalizedDescriptions(
    row: RuleDetailsRowData,
    workspaceDescriptions: NormalizedDescriptionInfo[],
): RuleDetailsRowData {
    if (workspaceDescriptions.length === 0) {
        return row;
    }
    const merged = new Map<string, NormalizedDescriptionInfo>();
    for (const desc of row.normalizedDescriptions) {
        merged.set(desc.id, desc);
    }
    for (const desc of workspaceDescriptions) {
        if (!merged.has(desc.id)) {
            merged.set(desc.id, desc);
        }
    }
    return {
        ...row,
        normalizedDescriptions: Array.from(merged.values()),
    };
}

export const HistoryRuleDetails: React.FC<HistoryRuleDetailsProps> = ({
    versions,
    footer,
}) => {
    const { data: normalizedDescriptionsData } = useNormalizedDescriptions();
    const workspaceNormalizedDescriptions = useMemo<
        NormalizedDescriptionInfo[]
    >(
        () =>
            (normalizedDescriptionsData?.normalizedDescriptions ?? []).map(
                (desc) => ({
                    id: desc.normalizedDescriptionId,
                    normalizedDescription: desc.normalizedDescription,
                }),
            ),
        [normalizedDescriptionsData],
    );
    const rows = useMemo<RuleDetailsRowData[]>(
        () =>
            versions
                .map((v) => buildRuleRowData(v))
                .map((row) =>
                    enrichRowWithNormalizedDescriptions(
                        row,
                        workspaceNormalizedDescriptions,
                    ),
                ),
        [versions, workspaceNormalizedDescriptions],
    );
    const isTransferRuleDetails = useMemo(
        () => rows.some((row) => row.ruleType === RuleGroupType.TRANSFER),
        [rows],
    );
    const activeDefaultView = isTransferRuleDetails
        ? defaultTransferHistoryRuleDetailsView
        : defaultHistoryRuleDetailsView;
    const activeConfig = isTransferRuleDetails
        ? transferHistoryRuleDetailsTableViewConfig
        : historyRuleDetailsTableViewConfig;
    const tableConfig = activeConfig as ViewConfig<
        RuleDetailsRowData,
        any,
        never
    >;

    const [view, setView] = useState<View>(activeDefaultView);
    const [prevActiveDefaultView, setPrevActiveDefaultView] =
        useState(activeDefaultView);
    if (activeDefaultView !== prevActiveDefaultView) {
        setPrevActiveDefaultView(activeDefaultView);
        setView(activeDefaultView);
    }

    if (rows.length === 0) {
        return <EmptyState header="No rule changes available" />;
    }

    return (
        <ViewTable
            data={rows}
            view={view}
            config={tableConfig}
            onViewChange={setView}
            emptyView={() => <EmptyState header="No rule changes available" />}
            footer={footer}
            className={historyDetailsStyles.historyDetailsView}
        />
    );
};
